// Application Configuration
export const APP_CONFIG = {
  NAME: 'Dukene',
  DESCRIPTION: 'Multi-tenant E-commerce Platform',
  VERSION: '1.0.0',
  DEFAULT_CURRENCY: 'KES',
  DEFAULT_TIMEZONE: 'Africa/Nairobi',
  DEFAULT_LANGUAGE: 'en',
} as const;

// API Configuration
export const API_CONFIG = {
  TIMEOUT: 30000,
  RETRY_ATTEMPTS: 3,
  RETRY_DELAY: 1000,
} as const;

// Pagination
export const PAGINATION = {
  DEFAULT_PAGE: 1,
  DEFAULT_LIMIT: 20,
  MAX_LIMIT: 100,
  LIMITS: [10, 20, 50, 100],
} as const;

// Routes - Storefront
export const STOREFRONT_ROUTES = {
  HOME: '/',
  PRODUCTS: '/products',
  PRODUCT_DETAIL: '/products/:slug',
  CATEGORIES: '/categories',
  CATEGORY_DETAIL: '/categories/:slug',
  CART: '/cart',
  CHECKOUT: '/checkout',
  CHECKOUT_SUCCESS: '/checkout/success',
  LOGIN: '/login',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  ACCOUNT: '/account',
  ACCOUNT_ORDERS: '/account/orders',
  ACCOUNT_ORDER_DETAIL: '/account/orders/:id',
  ACCOUNT_ADDRESSES: '/account/addresses',
  ACCOUNT_SETTINGS: '/account/settings',
  SEARCH: '/search',
  ABOUT: '/about',
  CONTACT: '/contact',
  TERMS: '/terms',
  PRIVACY: '/privacy',
  TRACK_ORDER: '/track',
} as const;

// Routes - Merchant Dashboard
export const MERCHANT_ROUTES = {
  DASHBOARD: '/',
  PRODUCTS: '/products',
  PRODUCTS_NEW: '/products/new',
  PRODUCTS_EDIT: '/products/:id/edit',
  CATEGORIES: '/categories',
  CATEGORIES_DETAIL: '/categories/:id',
  INVENTORY: '/inventory',
  ORDERS: '/orders',
  ORDERS_DETAIL: '/orders/:id',
  CUSTOMERS: '/customers',
  CUSTOMERS_DETAIL: '/customers/:id',
  ANALYTICS: '/analytics',
  SETTINGS: '/settings',
  SETTINGS_STORE: '/settings/store',
  SETTINGS_PAYMENTS: '/settings/payments',
  SETTINGS_SHIPPING: '/settings/shipping',
  SALES_CHANNELS: '/sales-channels',
  META_COMMERCE: '/meta-commerce',
  GOOGLE_COMMERCE: '/google-commerce',
  WHATSAPP_CATALOG: '/whatsapp-catalog',
  PROFILE: '/profile',
  NOTIFICATIONS: '/notifications',
  BILLING: '/billing',
  BILLING_WALLET: '/billing/wallet',
  SETTINGS_WALLET: '/settings/wallet',
  AUTOMATIONS: '/automations',
  SETTINGS_STORES_NEW: '/settings/stores/new',
  CHECKOUT_LINKS: '/payment-links',
} as const;

// Routes - SuperAdmin
export const SUPERADMIN_ROUTES = {
  DASHBOARD: '/',
  ORGANISATIONS: '/organisations',
  STORES: '/stores',
  STORES_DETAIL: '/stores/:id',
  USERS: '/users',
  USERS_DETAIL: '/users/:id',
  AFFILIATES: '/affiliates',
  AFFILIATES_CREATE: '/affiliates/create',
  AFFILIATES_DETAIL: '/affiliates/:id',
  SUPPLIERS: '/suppliers',
  SUPPLIERS_NEW: '/suppliers/new',
  SUPPLIERS_DETAIL: '/suppliers/:id',
  WHATSAPP: '/whatsapp',
  SHIPPING: '/shipping',
  ORDER_ROUTES: '/order-routes',
  ANALYTICS: '/analytics',
  METRICS: '/metrics',
  PAYMENTS: '/payments',
  REVENUE: '/revenue',
  SYSTEM: '/system',
  EMAIL: '/email',
  EMAIL_COMPOSE: '/email/compose',
  EMAIL_DETAIL: '/email/:id',
  SETTINGS: '/settings',
  PROFILE: '/profile',
} as const;

// Routes - Affiliate Dashboard
export const AFFILIATE_ROUTES = {
  DASHBOARD: '/',
  LINKS: '/links',
  COMMISSIONS: '/commissions',
  ANALYTICS: '/analytics',
  PAYOUTS: '/payouts',
  PROFILE: '/profile',
  APPLY: '/apply',
} as const;

// Order Status Colors
export const ORDER_STATUS_COLORS = {
  PENDING: 'orange',
  CONFIRMED: 'blue',
  PROCESSING: 'purple',
  SHIPPED: 'cyan',
  DELIVERED: 'green',
  CANCELLED: 'red',
  REFUNDED: 'gray',
} as const;

// Payment Status Colors
export const PAYMENT_STATUS_COLORS = {
  PENDING: 'orange',
  PAID: 'green',
  FAILED: 'red',
  REFUNDED: 'gray',
  PARTIALLY_REFUNDED: 'yellow',
} as const;

// Fulfillment Status Colors
export const FULFILLMENT_STATUS_COLORS = {
  UNFULFILLED: 'orange',
  PARTIALLY_FULFILLED: 'yellow',
  FULFILLED: 'green',
} as const;

// Product Status Colors
export const PRODUCT_STATUS_COLORS = {
  ACTIVE: 'green',
  DRAFT: 'gray',
  ARCHIVED: 'red',
} as const;

// Store Status Colors
export const STORE_STATUS_COLORS = {
  ACTIVE: 'green',
  INACTIVE: 'gray',
  SUSPENDED: 'red',
} as const;

// Validation Rules
export const VALIDATION = {
  EMAIL: {
    MIN_LENGTH: 5,
    MAX_LENGTH: 255,
    REGEX: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  },
  PASSWORD: {
    MIN_LENGTH: 8,
    MAX_LENGTH: 128,
    REQUIRE_UPPERCASE: true,
    REQUIRE_LOWERCASE: true,
    REQUIRE_NUMBER: true,
    REQUIRE_SPECIAL: false,
  },
  PHONE: {
    MIN_LENGTH: 10,
    MAX_LENGTH: 15,
    KENYA_REGEX: /^(0|254|\+254)[17]\d{8}$/,
  },
  PRODUCT: {
    NAME_MIN_LENGTH: 3,
    NAME_MAX_LENGTH: 255,
    DESCRIPTION_MAX_LENGTH: 5000,
    SKU_MIN_LENGTH: 3,
    SKU_MAX_LENGTH: 50,
    MIN_PRICE: 0,
    MAX_PRICE: 10000000,
    MAX_IMAGES: 10,
  },
  STORE: {
    NAME_MIN_LENGTH: 3,
    NAME_MAX_LENGTH: 100,
    SUBDOMAIN_MIN_LENGTH: 3,
    SUBDOMAIN_MAX_LENGTH: 63,
    SUBDOMAIN_REGEX: /^[a-z0-9]([a-z0-9-]{1,61}[a-z0-9])?$/,
  },
  ORDER: {
    MIN_AMOUNT: 1,
    MAX_ITEMS: 100,
  },
} as const;

// Date Formats
export const DATE_FORMATS = {
  SHORT: 'MMM dd, yyyy',
  LONG: 'MMMM dd, yyyy',
  WITH_TIME: 'MMM dd, yyyy HH:mm',
  ISO: "yyyy-MM-dd'T'HH:mm:ss",
  TIME_ONLY: 'HH:mm',
} as const;

// Image Sizes
export const IMAGE_SIZES = {
  THUMBNAIL: { width: 150, height: 150 },
  SMALL: { width: 300, height: 300 },
  MEDIUM: { width: 600, height: 600 },
  LARGE: { width: 1200, height: 1200 },
  BANNER: { width: 1920, height: 600 },
} as const;

// File Upload Limits
export const UPLOAD_LIMITS = {
  MAX_FILE_SIZE: 5 * 1024 * 1024, // 5MB
  ALLOWED_IMAGE_TYPES: ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'],
  ALLOWED_DOCUMENT_TYPES: ['application/pdf', 'application/msword'],
} as const;

// Kenya Counties
export const KENYA_COUNTIES = [
  'Baringo',
  'Bomet',
  'Bungoma',
  'Busia',
  'Elgeyo-Marakwet',
  'Embu',
  'Garissa',
  'Homa Bay',
  'Isiolo',
  'Kajiado',
  'Kakamega',
  'Kericho',
  'Kiambu',
  'Kilifi',
  'Kirinyaga',
  'Kisii',
  'Kisumu',
  'Kitui',
  'Kwale',
  'Laikipia',
  'Lamu',
  'Machakos',
  'Makueni',
  'Mandera',
  'Marsabit',
  'Meru',
  'Migori',
  'Mombasa',
  'Murang\'a',
  'Nairobi',
  'Nakuru',
  'Nandi',
  'Narok',
  'Nyamira',
  'Nyandarua',
  'Nyeri',
  'Samburu',
  'Siaya',
  'Taita-Taveta',
  'Tana River',
  'Tharaka-Nithi',
  'Trans-Nzoia',
  'Turkana',
  'Uasin Gishu',
  'Vihiga',
  'Wajir',
  'West Pokot',
] as const;

// Payment Methods
export const PAYMENT_METHODS = {
  MPESA: {
    name: 'M-Pesa',
    icon: '📱',
    description: 'Pay with M-Pesa mobile money',
  },
  CARD: {
    name: 'Credit/Debit Card',
    icon: '💳',
    description: 'Pay with Visa, Mastercard, or American Express',
  },
  CASH: {
    name: 'Cash on Delivery',
    icon: '💵',
    description: 'Pay with cash when your order is delivered',
  },
} as const;

// Shipping Methods
export const SHIPPING_METHODS = {
  STANDARD: {
    name: 'Standard Delivery',
    duration: '3-5 business days',
    cost: 200,
  },
  EXPRESS: {
    name: 'Express Delivery',
    duration: '1-2 business days',
    cost: 500,
  },
  SAME_DAY: {
    name: 'Same Day Delivery',
    duration: 'Within 24 hours',
    cost: 1000,
  },
} as const;

// Default Colors
export const DEFAULT_COLORS = {
  PRIMARY: '#3B82F6',
  SECONDARY: '#10B981',
  ACCENT: '#F59E0B',
  DANGER: '#EF4444',
  WARNING: '#F59E0B',
  SUCCESS: '#10B981',
  INFO: '#3B82F6',
} as const;

// Tax Rates
export const TAX_RATES = {
  STANDARD: 16, // Kenya VAT rate
  EXEMPT: 0,
} as const;

// Cache Keys
export const CACHE_KEYS = {
  USER: 'user',
  STORE: 'store',
  CART: 'cart',
  PRODUCTS: 'products',
  CATEGORIES: 'categories',
  ORDERS: 'orders',
  CUSTOMERS: 'customers',
} as const;

// Cache Durations (in milliseconds)
export const CACHE_DURATIONS = {
  SHORT: 1 * 60 * 1000, // 1 minute
  MEDIUM: 5 * 60 * 1000, // 5 minutes
  LONG: 30 * 60 * 1000, // 30 minutes
  VERY_LONG: 60 * 60 * 1000, // 1 hour
} as const;

// Error Messages
export const ERROR_MESSAGES = {
  GENERIC: 'An unexpected error occurred. Please try again.',
  NETWORK: 'Network error. Please check your connection.',
  UNAUTHORIZED: 'You are not authorized to perform this action.',
  NOT_FOUND: 'The requested resource was not found.',
  VALIDATION: 'Please check your input and try again.',
  SERVER: 'Server error. Please try again later.',
} as const;

// Success Messages
export const SUCCESS_MESSAGES = {
  SAVED: 'Changes saved successfully',
  CREATED: 'Created successfully',
  UPDATED: 'Updated successfully',
  DELETED: 'Deleted successfully',
  SENT: 'Sent successfully',
} as const;

// Local Storage Keys
export const STORAGE_KEYS = {
  ACCESS_TOKEN: 'accessToken',
  REFRESH_TOKEN: 'refreshToken',
  USER: 'user',
  CART: 'cart',
  RECENT_SEARCHES: 'recentSearches',
  THEME: 'theme',
  LANGUAGE: 'language',
} as const;

// Analytics Events
export const ANALYTICS_EVENTS = {
  PAGE_VIEW: 'page_view',
  PRODUCT_VIEW: 'product_view',
  ADD_TO_CART: 'add_to_cart',
  REMOVE_FROM_CART: 'remove_from_cart',
  BEGIN_CHECKOUT: 'begin_checkout',
  PURCHASE: 'purchase',
  SEARCH: 'search',
  SIGN_UP: 'sign_up',
  LOGIN: 'login',
} as const;

// Debounce Delays
export const DEBOUNCE_DELAYS = {
  SEARCH: 300,
  INPUT: 500,
  RESIZE: 200,
  SCROLL: 100,
} as const;

// Plan Limits
export const PLAN_LIMITS = {
  BASIC: 1,
  PRO: 2,
  MAX: 5,
  ENTERPRISE: 999,
} as const;

// Sales Channel Limits per store
export const SALES_CHANNEL_LIMITS = {
  BASIC: 1,
  PRO: 3,
  MAX: 999,
  ENTERPRISE: 999,
} as const;

// Platform Transaction Fees (%)
export const PLAN_TRANSACTION_FEES = {
  BASIC: 6,
  PRO: 3,
  MAX: 2,
  ENTERPRISE: 0,
} as const;

// Plan Monthly Pricing (KES)
export const PLAN_PRICING = {
  BASIC: 0,
  PRO: 3500,
  MAX: 9500,
  ENTERPRISE: null,
} as const;
